import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { MDParty } from "../data/MDParty";
import { getConfig } from "../config/apiConfig";
import axios from "axios";
import NexratesCounterpartiesListData from "./NexratesCounterpartiesListData";
import NexratesCounterpartiesListFilter from "./NexratesCounterpartiesListFilter";
import { PageWrapperDTO } from "../data/PageWrapperDTO";
import { useAuth } from "react-oidc-context";

export interface NexratesCounterpartiesListHandle {
    reloadData: () => void;
}

const NexratesCounterpartiesList = forwardRef<NexratesCounterpartiesListHandle ,{
    onClickView: (code: string) => void,
    onClickEdit: (code: string) => void,
    onClickCreate: () => void
}>(({onClickView, onClickEdit, onClickCreate}, ref) => {

    const API_BASE_URL = getConfig().REACT_APP_API_BASE_URL;

    const [data, setData] = useState<PageWrapperDTO<MDParty> | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean | undefined>(undefined);
    const [isError, setIsError] = useState<boolean | undefined>(undefined);
    const [isAuthorized, setIsAuthorized] = useState<boolean | undefined>(undefined);
    const [pageNum, setPageNum] = useState<number>(0);
    const [filter, setFilter] = useState<{code: string, description: string, salesBook: string, site: string, showInactive: boolean}>({code: "", description: "", salesBook: "", site: "", showInactive: false});

    const auth = useAuth();
    const token = auth.user?.access_token;

    const fetchData = useCallback(async (code: string, description: string, salesBook: string, site: string, showInactive: boolean, pageNum: number) => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/parties`, {params: {
                filterCode: code, filterDescription: description, filterSalesBook: salesBook, filterSite: site, filterShowInactive: showInactive, pageNum: pageNum, pageSize: 10
            }, headers: {Authorization: `Bearer ${token}`}});
            setData(response.data);
            setIsAuthorized(true);
            setIsError(false);
        } catch (e: any) {
            console.log("the error: " + e);
            if (axios.isAxiosError(e) && e.response && e.response.status === 403) {
                setIsAuthorized(false);
                setIsError(false);
            } else {
                setIsError(true);
            }            
        } finally {
            setIsLoading(false);
        }
    }, [token, API_BASE_URL]);
    
    useEffect(() => {
        fetchData(filter.code, filter.description, filter.salesBook, filter.site, filter.showInactive, pageNum);
    }, [filter.code, filter.description, filter.salesBook, filter.site, filter.showInactive, fetchData, pageNum]);

    const handleFilterChange = useCallback((code: string, description: string, salesBook: string, site: string, showInactive: boolean) => {
        console.log("Filter needs to be reapplied: " + code + "," + description + "," + salesBook + "," + site + "," + showInactive);
        setPageNum(0);
        setFilter({code: code, description: description, salesBook: salesBook, site: site, showInactive: showInactive});
    }, []);

    const handleDisable = async (code: string) => {
        try {
            await axios.put(`${API_BASE_URL}/parties/${code}/disable`, null, {headers: {Authorization: `Bearer ${token}`}});
            fetchData(filter.code, filter.description, filter.salesBook, filter.site, filter.showInactive, pageNum);
            alert("Successfully deactivated!");
        } catch {
            alert("Not possible to disable!");
        }
    }

    const handleEnable = async (code: string) => {
        try {
            await axios.put(`${API_BASE_URL}/parties/${code}/enable`, null, {headers: {Authorization: `Bearer ${token}`}});
            fetchData(filter.code, filter.description, filter.salesBook, filter.site, filter.showInactive, pageNum);
            alert("Successfully activated!");
        } catch {
            alert("Not possible to disable!");
        }
    }

    const reloadData = () => {
        fetchData(filter.code, filter.description, filter.salesBook, filter.site, filter.showInactive, pageNum);
    }

    useImperativeHandle(ref, () => ({
        reloadData
    }))

    if (isAuthorized === false) {
        return <p>The user is not authorized!</p>;
    }

    return (
        <>
            <table className="table table-striped table-hover">
                <thead>
                    <tr>
                        <th className="col-2">Code</th>
                        <th className="col-4">Description</th>
                        <th className="col-2">Sales book</th>
                        <th className="col-1">Site</th>
                        <th className="col-3">Actions</th>
                    </tr>
                    <NexratesCounterpartiesListFilter onFilterChange={handleFilterChange} />
                </thead>
                <tbody>
                    {isLoading && <p>Loading...</p>}
                    {isError && <p>Error!</p>}
                    {!isLoading && !isError && isAuthorized && data !== undefined &&
                        <NexratesCounterpartiesListData data={data.elements}
                        onClickView={(code) => onClickView(code)} onClickEdit={(code) => onClickEdit(code)}
                        onClickEnable={(code) => handleEnable(code)} onClickDisable={(code) => handleDisable(code)}
                        />}
                </tbody>
            </table>
            <div className="container d-flex justify-content-between align-items-center">
                <div className="position-absolute start-50 translate-middle-x">
                    {data !== undefined && <>
                        <button className="btn btn-info btn-sm me-2" onClick={() => setPageNum(0)}>
                            <i className="bi bi-chevron-double-left"></i>
                        </button>
                        <button className="btn btn-info btn-sm me-2" onClick={() => setPageNum(pg => Math.max(pg - 1, 0))}>
                            <i className="bi bi-chevron-left"></i>
                        </button>
                        <span className="me-2">Page {data.numberOfPage + 1} of {data.totalPages}</span>
                        <button className="btn btn-info btn-sm me-2" onClick={() => setPageNum(pg => Math.min(pg + 1, data.totalPages - 1))}>
                            <i className="bi bi-chevron-right"></i>
                        </button>
                        <button className="btn btn-info btn-sm me-2" onClick={() => setPageNum(data.totalPages - 1)}>
                            <i className="bi bi-chevron-double-right"></i>
                        </button>
                    </>}
                </div>
                <button className="btn btn-success ms-auto" onClick={() => onClickCreate()}>Create</button>
            </div>            
        </>
    );
});

export default React.memo(NexratesCounterpartiesList);